// These represent types of content across the app.
// A little bit vague, but mostly these are concrete things (nouns)
// that a notification might be linked to.
export enum ContentType {
  POST = 'POST',
  COMMENT = 'COMMENT',
  PROJECT_COMMENT = 'PROJECT_COMMENT',
  LIKE = 'LIKE',
  PROJECT_LIKE = 'PROJECT_LIKE',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  HACKATHON_PROJECT = 'HACKATHON_PROJECT',
}

export const ContentTypeDisplay = {
  [ContentType.POST]: 'Post',
  [ContentType.COMMENT]: 'Comment',
  [ContentType.PROJECT_COMMENT]: 'Comment',
  [ContentType.LIKE]: 'Like',
  [ContentType.PROJECT_LIKE]: 'Like',
  [ContentType.ANNOUNCEMENT]: 'Announcement',
  [ContentType.HACKATHON_PROJECT]: 'Hackathon Project',
} as const satisfies Record<ContentType, string>;

// These are actions a user might be notified about,
// and represent unique "interactions" as defined by
// the application layer.
//
// Unlike the nouns above, they are not necessarily
// stored as separate data models in the database
// (e.g. a mention is just a comment with an @username tag in it)
export enum InteractionType {
  LIKE = 'LIKE',
  PROJECT_LIKE = 'PROJECT_LIKE',
  COMMENT = 'COMMENT',
  PROJECT_COMMENT = 'PROJECT_COMMENT',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
  // MENTION = 'MENTION',
  // REPOST = 'REPOST',
  // SAVE = 'SAVE', ??
  // SHARE = 'SHARE', ??
  TEAM_MEMBER_ADDED = 'TEAM_MEMBER_ADDED',
}

export enum NotificationSettingsType {
  CONTENT_LIKE = 'CONTENT_LIKE',
  PROJECT_LIKE = 'PROJECT_LIKE',
  CONTENT_COMMENT = 'CONTENT_COMMENT',
  PROJECT_COMMENT = 'PROJECT_COMMENT',
  ANNOUNCEMENT = 'ANNOUNCEMENT',
}

// This might seem unnecessarily convoluted, but it's a way to create a higher-level setting for the user,
// given our underlying internal data model. Without this, we would basically have to expose every single combination
// of interactionType and contentType as a setting, which would be a lot of settings, and probably not very useful for people.
//
// This way, we can create a UI toggle like:
//
// - "Notify me when someone likes my content"
//
// Which will match likes on posts, comments, AND project comments, and in the future maybe other forum content types.
export const NotificationSettings = {
  [NotificationSettingsType.CONTENT_LIKE]: [
    [[InteractionType.LIKE], [ContentType.POST, ContentType.COMMENT]],
    [[InteractionType.PROJECT_LIKE], [ContentType.PROJECT_COMMENT]],
  ],
  [NotificationSettingsType.PROJECT_LIKE]: [
    [[InteractionType.PROJECT_LIKE], [ContentType.HACKATHON_PROJECT]],
  ],
  [NotificationSettingsType.CONTENT_COMMENT]: [
    [[InteractionType.COMMENT], [ContentType.POST, ContentType.COMMENT]],
    [[InteractionType.PROJECT_COMMENT], [ContentType.PROJECT_COMMENT]],
  ],
  [NotificationSettingsType.PROJECT_COMMENT]: [
    [[InteractionType.PROJECT_COMMENT], [ContentType.HACKATHON_PROJECT]],
  ],
  [NotificationSettingsType.ANNOUNCEMENT]: [
    [[InteractionType.ANNOUNCEMENT], [ContentType.ANNOUNCEMENT]],
  ],
} as const satisfies Record<
  NotificationSettingsType,
  [InteractionType[], ContentType[]][]
>;

// Take in a notification type as described by its interactionType and contentType
// and return the corresponding notification settings key
export const notificationToSettingsKey = (
  interactionType: InteractionType,
  contentType: ContentType,
) => {
  return Object.keys(NotificationSettings).find((key) =>
    NotificationSettings[key as NotificationSettingsType].some(
      ([interactionTypes, contentTypes]: [InteractionType[], ContentType[]]) =>
        interactionTypes.includes(interactionType) &&
        contentTypes.includes(contentType),
    ),
  );
};

export interface Notification {
  id: number;
  contentId: number;
  postId: number;
  contentType: ContentType;
  contentBody?: string;
  interactionBody?: string;
  interactionType: InteractionType;
  interactionId?: number;
  interactionCount: number;
  urlSlug?: string;
  createdAt: Date;
  isRead: boolean;
  interactingUsers?: {
    username: string;
    displayName: string;
    avatarUrl: string;
  }[];
}

export enum ForumSummaryFrequency {
  NONE = 'NONE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
}
